import { Box, Skeleton, Flex } from '@chakra-ui/react';
import { Button, Heading, Card, CardBody } from 'components/v4';
import { useNavigate, useParams } from 'react-router-dom';

import { axios } from '@/services/axios';
import { useQuery } from 'hooks/useCustomQuery';
import { differenceBy } from 'lodash';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import ServiceStatus from '../users/ServiceStatus';
import TransferringTables from 'components/tables/TransferringTables/TransferringTables';
import config from '@/config.json';

export default function ActivateServiceNumbers() {
    const { id: serviceId } = useParams();
    const navigate = useNavigate();

    const { currentCompany } = useSelector((state) => {
        return { ...state.navigationLists };
    });
    const { darkMode } = useSelector((state) => state.settings);

    // left side table (numbers pool)
    const [numbersAvailable, setNumbersAvailable] = useState([]);
    // right side table (activated/yet to be activated numbers)
    const [numbersChosen, setNumbersChosen] = useState([]);
    // service numbers that are already activated
    const [activatedNumbers, setActivatedNumbers] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // service status
    const [success, setSuccess] = useState(0);
    const [failed, setFailed] = useState(0);
    const [activateError, setActivateError] = useState({
        count: 0,
        message: '',
    });
    const [deactivateError, setDeactivateError] = useState({
        count: 0,
        message: '',
    });
    const [total, setTotal] = useState(0);

    // get users available to be activated for the service
    const { data: service, isFetching: serviceLoading } = useQuery({
        queryKey: ['service', serviceId],
        queryFn: async () => {
            const res = await axios.get(`service/${serviceId}`);
            return res.data;
        },
        enabled: Boolean(serviceId),
        refetchOnWindowFocus: false,
    });

    // get numbers available to be activated for the service
    const { data: companyNumbers, isFetching: companyNumbersLoading } =
        useQuery({
            queryKey: ['companyNumbers'],
            queryFn: async () => {
                const res = await axios.get(
                    `company/${currentCompany}/numbers`,
                );
                return res.data?.numbers || [];
            },
            enabled: Boolean(currentCompany),
            onSuccess: async (data) => {
                // numbers avaialble to be activated
                const transformedData = data.map((number) => {
                    const statusText =
                        config.numbers.statusJSON[String(number.status)] ||
                        String(number.status);

                    return {
                        ...number,
                        statusText,
                    };
                });
                setNumbersAvailable(transformedData);
            },
            onError: (err) => {
                toast.error(err?.response?.data?.message);
            },
            refetchOnWindowFocus: false,
        });

    // get activated numbers for the service
    const { isFetching: serviceNumbersLoading } = useQuery({
        queryKey: ['serviceNumbers', serviceId],
        queryFn: async () => {
            const res = await axios.get(
                `service/vendordefined/${serviceId}/numbers`,
            );
            return res.data;
        },
        // service id, company numbers is not loading and company numbers is greater than 0, then fetch service numbers
        enabled:
            Boolean(serviceId) &&
            !companyNumbersLoading &&
            companyNumbers?.length > 0,
        onSuccess: async (data) => {
            if (numbersAvailable) {
                let ua = [...numbersAvailable];
                let numbersChosen = [];

                ua = ua.filter((number) => {
                    if (data.includes(number.id)) {
                        numbersChosen.push(number);
                        return false; // Exclude this number from numbersAvailable
                    }
                    return true; // Keep this number in numbersAvailable
                });

                // Push filtered available numbers
                setNumbersAvailable(ua);
                // Push chosen numbers into numbersChosen
                setNumbersChosen(numbersChosen);
                // numbers that are already activated
                setActivatedNumbers(numbersChosen);
            }
        },
        onError: (err) => {
            toast.error(err?.response?.data?.message);
        },
        refetchOnWindowFocus: false,
    });

    const handleBackButton = () => {
        setIsLoading(false);
        setActivateError({
            count: 0,
            message: '',
        });
        setDeactivateError({
            count: 0,
            message: '',
        });
        setTotal(0);
        setSuccess(0);
        setFailed(0);
        navigate('/dashboard-services');
    };

    const handleSubmit = async () => {
        setIsLoading(true);
        let failedDeactivateNumbers = [];
        let failedActivateNumbers = [];
        try {
            let activateNumbers = differenceBy(
                numbersChosen,
                activatedNumbers,
                'id',
            );
            let deactivateNumbers = differenceBy(
                activatedNumbers,
                numbersChosen,
                'id',
            );

            setTotal(activateNumbers.length + deactivateNumbers.length);

            // activate service numbers
            for (const number of activateNumbers) {
                try {
                    await axios.post(
                        `/service/vendordefined/${serviceId}/number/${number.id}/activate`,
                    );
                    setSuccess((prev) => prev + 1);
                } catch (e) {
                    setFailed((prev) => prev + 1);
                    failedActivateNumbers.push(number);
                }
            }

            // deactivate service numbers
            for (const number of deactivateNumbers) {
                try {
                    await axios.post(
                        `/service/vendordefined/${serviceId}/number/${number.id}/deactivate`,
                    );
                    setSuccess((prev) => prev + 1);
                } catch (e) {
                    setFailed((prev) => prev + 1);
                    failedDeactivateNumbers.push(number);
                }
            }
        } catch (e) {
            toast.error(e?.response?.data?.message);
        } finally {
            // setIsLoading(false);
            if (failedActivateNumbers.length > 0) {
                let message = '';
                for (const [index, number] of failedActivateNumbers.entries()) {
                    if (index !== failedActivateNumbers.length - 1) {
                        message = message.concat(number.telephoneNumber + ', ');
                    } else {
                        message = message.concat(number.telephoneNumber + '.');
                    }
                }
                setActivateError((prevState) => ({
                    ...prevState,
                    count: failedActivateNumbers.length,
                    message: message,
                }));
            }

            if (failedDeactivateNumbers.length > 0) {
                let message = '';
                for (const [
                    index,
                    number,
                ] of failedDeactivateNumbers.entries()) {
                    if (index !== failedDeactivateNumbers.length - 1) {
                        message = message.concat(number.telephoneNumber + ', ');
                    } else {
                        message = message.concat(number.telephoneNumber + '.');
                    }
                }
                setDeactivateError((prevState) => ({
                    ...prevState,
                    count: failedDeactivateNumbers.length,
                    message: message,
                }));
            }
        }
    };

    const numberColDefs = [
        {
            label: 'Number',
            dataKey: 'telephoneNumber',
            minWidth: 200,
            flexGrow: 1,
            maxWidth: 300,
            cellRenderer: ({ rowData }) => {
                const number = rowData?.telephoneNumber;
                return number ? `+${number}` : '';
            },
        },
        {
            label: 'Status',
            dataKey: 'statusText',
            width: 200,
            minWidth: 200,
            flexGrow: 0,
            maxWidth: 200,
        },
        {
            isCheckbox: true,
            label: 'Action',
            dataKey: 'checkbox',
            width: 80,
            minWidth: 80,
            maxWidth: 80,
        },
    ];

    const availableConfig = {
        captionName: 'Available Numbers ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const chosenConfig = {
        captionName: 'Chosen Numbers ',
        placeholder: 'Search numbers...',
        colDefs: numberColDefs,
        searchField: 'telephoneNumber',
    };

    const walkthroughIds = {
        moveToChosen: '/service-profiles/button/move/chosen',
        moveToAvailable: '/service-profiles/button/move/available',
        moveAllToChosen: '/service-profiles/button/move/chosen/all',
        moveAllToAvailable: '/service-profiles/button/move/available/all',
    };

    return (
        <Box maxW="1200px">
            <Card
                bg={darkMode ? 'dark.tcap' : null}
                borderRadius="8px"
                boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)"
                mb={6}>
                <CardBody p={6}>
                    <Heading fontSize="2xl" fontWeight="bold" mb={4}>
                        Activate Service Numbers
                        {serviceLoading ||
                        companyNumbersLoading ||
                        serviceNumbersLoading ? (
                            <Skeleton
                                mt={1}
                                w={200}
                                h={8}
                                ml={2}
                                display="inline-block"
                            />
                        ) : (
                            <> : {service?.name || 'Vendor Defined'}</>
                        )}
                    </Heading>

                    {isLoading ? (
                        <ServiceStatus
                            title="Numbers"
                            failedCount={failed}
                            activateError={activateError}
                            deactivateError={deactivateError}
                            successCount={success}
                            totalCount={total}
                            handleBack={handleBackButton}
                        />
                    ) : (
                        <>
                            <TransferringTables
                                availableData={numbersAvailable}
                                chosenData={numbersChosen}
                                availableTableConfig={availableConfig}
                                chosenTableConfig={chosenConfig}
                                isLoading={
                                    companyNumbersLoading ||
                                    serviceNumbersLoading
                                }
                                isDisabled={isLoading}
                                onAvailableDataChange={setNumbersAvailable}
                                onChosenDataChange={setNumbersChosen}
                                walkthroughIds={walkthroughIds}
                            />

                            <Flex gap={4} mt={8} justifyContent="center">
                                <Button
                                    variant="outline"
                                    onClick={handleBackButton}
                                    isDisabled={isLoading}
                                    data-walkthroughid="/service-profiles/button/back">
                                    Back
                                </Button>
                                <Button
                                    onClick={handleSubmit}
                                    isLoading={isLoading}
                                    data-walkthroughid="/service-profiles/button/save">
                                    Save
                                </Button>
                            </Flex>
                        </>
                    )}
                </CardBody>
            </Card>
        </Box>
    );
}
